import {
    AnvilSelect,
    AnvilSelectProps,
    FilterButton,
    FilterButtonProps,
    Form,
    Link,
} from '@servicetitan/design-system';
import {
    AnvilSelectOptionsProps,
    TriggerProps,
} from '@servicetitan/design-system/dist/components/AnvilSelect/components';
import { FieldState } from 'formstate';
import { observer } from 'mobx-react';
import { FC, useMemo, useState } from 'react';
import { SelectNewState } from '../../../utils/select-new-state';

export class AnvilSelectFieldState<T> extends FieldState<T> {
    onChangeHandler = (value: AnvilSelectOptionsProps | AnvilSelectOptionsProps[] | undefined) => {
        this.onChange(Array.isArray(value) ? value.map(v => v.value) : value?.value);
    };
}

export const useClearLabel = (isEmpty: boolean, mobile: boolean, clear: () => void) =>
    useMemo(
        () =>
            !isEmpty && mobile
                ? {
                      action: (
                          <Link primary onClick={clear}>
                              Clear
                          </Link>
                      ),
                  }
                : undefined,
        [isEmpty, mobile, clear]
    );

interface PortalFilterFieldSelectNewProps extends PortalSelectNewProps {
    label?: string;
    mobile?: boolean;
    className?: string;
}

export const PortalFilterFieldSelectNew: FC<PortalFilterFieldSelectNewProps> = observer(props => {
    const { className, fieldState, label, mobile, ...otherProps } = props;

    const isEmptyValue = Array.isArray(fieldState.value)
        ? !fieldState.value.length
        : fieldState.value === undefined;

    const clear = () =>
        (
            fieldState.onChange as (
                newSelection: AnvilSelectOptionsProps | AnvilSelectOptionsProps[] | undefined
            ) => void
        )?.(undefined);

    const labelProps = useClearLabel(isEmptyValue, !!mobile, clear);

    return (
        <Form.Field label={label} labelProps={labelProps} className={className}>
            <PortalSelectNew label={label} fieldState={fieldState} {...otherProps} />
        </Form.Field>
    );
});

interface PortalSelectNewProps extends Omit<AnvilSelectProps, 'value' | 'trigger'> {
    label?: string;
    mobile?: boolean;
    searchPlaceholder?: string;
    state: SelectNewState;
    fieldState: AnvilSelectFieldState<any>;
    optionComponent?: FC<{ option: AnvilSelectOptionsProps }>;
    triggerComponent?: 'filter';
    triggerProps?: TriggerProps;
    filterButtonProps?: Omit<FilterButtonProps, 'label'>;
}

export const PortalSelectNew: FC<PortalSelectNewProps> = observer(props => {
    const {
        fieldState,
        filterButtonProps,
        label,
        mobile,
        onChange,
        optionComponent,
        options,
        searchPlaceholder,
        state,
        triggerComponent,
        triggerProps,
        ...otherProps
    } = props;

    const [open, setOpen] = useState(false);

    let selectOptions = state.options;
    if (optionComponent) {
        selectOptions = selectOptions.map((option: AnvilSelectOptionsProps) => {
            if (option.options) {
                return {
                    ...option,
                    options: option.options.map((subOption: AnvilSelectOptionsProps) => ({
                        ...subOption,
                        content: optionComponent({ option: subOption }),
                    })),
                };
            }
            return {
                ...option,
                content: optionComponent({ option }),
            };
        });
    }

    const selectValue = useMemo(() => {
        const compareValue = (option: AnvilSelectOptionsProps) =>
            Array.isArray(fieldState.value)
                ? fieldState.value.includes(option.value)
                : fieldState.value === option.value;

        if (!props.multiple) {
            return state.flatOptions.find((option: AnvilSelectOptionsProps) =>
                compareValue(option)
            );
        }

        return state.flatOptions.filter((option: AnvilSelectOptionsProps) => compareValue(option));
    }, [fieldState.value, props.multiple, state.flatOptions]);

    const triggerOptions = {
        clearable: !mobile,
        ...(triggerProps ?? {}),
    };

    let triggerElement;
    if (triggerComponent === 'filter') {
        triggerElement = (
            <FilterButton label={label ?? 'Select'} expandIcon={open} {...filterButtonProps} />
        );
    }

    const handleOnChange = (
        value: AnvilSelectOptionsProps | AnvilSelectOptionsProps[] | undefined
    ) => {
        fieldState.onChangeHandler(value);
        onChange?.(value);
    };

    const handleOpenChange = (isOpen: boolean) => {
        setOpen(isOpen);
        state.setSearch('');
    };

    return (
        <AnvilSelect
            search={{
                placeholder: searchPlaceholder,
                value: state.search,
                onChange: state.onSearchChange,
            }}
            value={selectValue}
            options={selectOptions}
            onChange={handleOnChange}
            scrollHeight="300px"
            popoverWidth="100"
            trigger={triggerElement ? triggerElement : triggerOptions}
            onOpenChange={handleOpenChange}
            {...otherProps}
        />
    );
});
